import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import {
  CoreApi,
  findLowestPriceProduct,
  Product,
  productsHaveSamePrice,
  ProductType
} from '@inter-ikea-storageone/range';
import { reportOpenProductInformation } from 'analytics/reporters';
import * as LiveAnnouncer from '@react-aria/live-announcer';
import { getProductInSceneCount } from 'ducks/products';
import { RootState } from 'ducks';
import useIsRtl from 'hooks/useIsRtl';
import { getDefaultHoverImage, getMPPImage, getTranslatedTitle } from 'products';
import { DraggableProduct } from 'products/interfaces/DraggableProduct';
import { Behaviors, ProductCardIcon, ProductCardImage } from 'rangeConfig/@types';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';
import Box from 'ui/Box';
import Body from 'ui/Typography/Body';
import { imageHoverTransition } from 'utils/imageUtils';
import DraggableWrapper from '../DraggableWrapper/DraggableWrapper';
import CheckboxIcon from './CheckboxIcon';
import useOnInfoClicked from 'components/ProductSidebar/MobileMenu/utils/useOnInfoClicked';
import InfoButton from '../InfoButton/InfoButton';
import Availability from './Availability';
import { useAvailability } from 'contexts/availability';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import PriceModule from 'ui/PriceModule/PriceModule';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import Price from 'ui/Price/Price';
import { setLastFocusedElementId } from 'utils/focusRestore';
import { getQuery } from 'utils/queryUtils';
import Icon from 'ui/Icon/Icon';
import { TotalPriceCalculator } from '@inter-ikea-kompis/utilities';
import ProductImage from 'components/ProductImage/ProductImage';
import { useCore } from 'contexts/core';
import SkapaTooltip from 'ui/Tooltip/SkapaTooltip';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useWidget } from 'contexts/widget';
import { useServiceSettings } from 'contexts/serviceSettings';

const StyledCard = styled.div`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 100%;
  border: none;
`;

const StyledButton = styled.button`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 100%;
  border: none;
  text-align: left;
  transition: ${imageHoverTransition()};
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);
  padding: 10px;

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
    border-radius: 5px;
  }

  &[data-is-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledHeader = styled.div``;

const CheckboxIconWrapper = styled.div`
  position: absolute;
  top: 0;
  display: block;
  z-index: 9;
  padding: ${({ theme }) => theme.space.xs};
`;

const IconTypeWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.space.xs};
  z-index: 9;
  padding: ${({ theme }) => theme.space.s};
  background-color: ${({ theme }) => theme.colors.grey.modal};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledBox = styled(Box)`
  text-align: left;
`;

const ImageWrapper = styled.div``;

const CardInformationWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const InfoButtonWrapper = styled(Box)`
  display: flex;
  align-items: flex-start;
  position: absolute;
`;

const StyledTitle = styled(Body).attrs({
  fontWeight: 'bold'
})`
  line-height: 1;
`;

const StyledSubtitle = styled(Body)`
  line-height: 1;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const PriceWrapper = styled.div`
  padding: 0.25rem 0;
`;

interface Props {
  product: Product;
  cardTitle?: string;
  cardSubtitle?: string | ((product: Product) => string);
  behaviors?: Behaviors;
  action?: 'add' | 'replace' | 'auto-adapt';
  cluster?: Product[];
  selectedGroupId?: string;
  showInfoBtn?: boolean;
  showPrice?: boolean;
  showAvailability?: boolean;
  productCardIcon?: ProductCardIcon | ((product: Product) => ProductCardIcon);
  selected?: (product: Product, coreApi: CoreApi) => boolean;
  infoButtonAriaLabel?: string;
  mainImage?: ProductCardImage;
  hoverImage?: ProductCardImage;
  handleClick?: (product: Product) => void;
  disabled?: boolean | undefined;
  children?: React.ReactNode;
  listIndex?: number;
}

const ProductCard = React.memo(
  ({
    product,
    action = 'add',
    cluster,
    cardTitle,
    cardSubtitle,
    behaviors,
    showInfoBtn = true,
    showPrice = true,
    showAvailability = true,
    productCardIcon,
    selected,
    infoButtonAriaLabel,
    mainImage,
    hoverImage,
    disabled,
    handleClick,
    listIndex,
    children
  }: Props) => {
    const { formatMessage } = useIntl();
    const widgetContext = useWidget();
    const behaviorTracker = useBehaviorTracker();
    const isRtl = useIsRtl();
    const coreApi = useCore();
    const { zipCode, storeId } = useAvailability();
    const [imageHeight, setImageHeight] = useState(0);
    const imageRef = useRef<HTMLDivElement>(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const padding = 10;
    const {
      dexfSettings: {
        localisation: { dateFormat }
      }
    } = useServiceSettings();

    const infoClick = useOnInfoClicked(product, cluster);
    const generateInteriorAssembly = getQuery().get('generateInteriorAssembly') === 'true';
    const generateFrontAssembly = getQuery().get('generateFrontAssembly') === 'true';
    if (generateInteriorAssembly || generateFrontAssembly) showPrice = false;

    const title = cardTitle ? getTranslatedTitle(cardTitle, product, formatMessage) : '';
    const subtitle =
      typeof cardSubtitle === 'string'
        ? getTranslatedTitle(cardSubtitle, product, formatMessage)
        : cardSubtitle
        ? getTranslatedTitle(cardSubtitle(product), product, formatMessage)
        : '';

    const productTitleforAriaLabelInInfoButton = infoButtonAriaLabel
      ? getTranslatedTitle(infoButtonAriaLabel, product, formatMessage)
      : title === ''
      ? subtitle
      : `${title} ${subtitle}`;

    const item: DraggableProduct = {
      type: 'product',
      targets: [product],
      params: { ...widgetContext, meta: { listIndex } },
      behaviors
    };

    const count = useSelector<RootState, number>(state => {
      return getProductInSceneCount(state, product.coreProductId, cluster);
    });
    const checked = selected !== undefined ? selected(product, coreApi) : count > 0;

    const scaleCounterProps = useSpring({
      opacity: checked ? 1 : 0,
      scale: checked ? 1 : 0,
      from: { opacity: 0, scale: 0 }
    });

    const [{ isDragging }, drag] = useDrag({
      item,
      collect: monitor => ({
        isDragging: monitor.isDragging()
      })
    });

    const mainCardImage = (mainImage && mainImage(product)) || getMPPImage(product);
    const hoverCardImage =
      hoverImage !== undefined ? hoverImage(product) : getDefaultHoverImage(product);

    const lowestPriceProduct = findLowestPriceProduct(cluster ? cluster : [product]);
    const { shoppingProducts } = useFetchShoppingProducts(
      lowestPriceProduct ? [lowestPriceProduct] : []
    );
    const onInfoClick = (event: React.MouseEvent<HTMLElement>): void => {
      setLastFocusedElementId('info-btn' + product.coreProductId);
      reportOpenProductInformation({ ...widgetContext, articleNumber: product.globalItemNo });
      event.stopPropagation();
      infoClick(event);
    };

    const productTypeIcon = (() => {
      if (!productCardIcon) return undefined;

      if (typeof productCardIcon === 'function') {
        return productCardIcon(product).icon;
      }

      return productCardIcon?.icon;
    })();

    const tooltipText =
      typeof productCardIcon === 'function'
        ? productCardIcon(product).tooltip
        : productCardIcon?.tooltip;

    const onLoad = () => {
      setImageLoaded(true);
    };

    useLayoutEffect(() => {
      if (imageRef.current && imageLoaded) {
        setImageHeight(imageRef.current.getBoundingClientRect().height);
      }
    }, [imageLoaded]);

    const announce = (text: string): void => {
      LiveAnnouncer.announce(text, 'polite');
    };

    const onClick = useCallback(() => {
      if (handleClick) {
        handleClick(product);
      } else if (behaviors) {
        announce(
          productTitleforAriaLabelInInfoButton +
            ' ' +
            formatMessage({ id: 'storageone.ariaAddToScene' })
        );
        if (cluster && action === 'auto-adapt') {
          behaviorTracker.withMeta({ listIndex }).trigger(
            'productClicked',
            {
              targets: cluster
            },
            behaviors
          );
        } else if (action === 'add' || action === 'replace') {
          announce(
            productTitleforAriaLabelInInfoButton +
              formatMessage({ id: 'storageone.ariaAddToScene' })
          );

          behaviorTracker.withMeta({ listIndex }).trigger(
            'productClicked',
            {
              targets: [product]
            },
            behaviors
          );
        }
      }
    }, [
      behaviorTracker,
      handleClick,
      product,
      productTitleforAriaLabelInInfoButton,
      formatMessage,
      cluster,
      action,
      behaviors,
      listIndex
    ]);

    const price = showPrice
      ? TotalPriceCalculator.calculateMultiple(shoppingProducts, dateFormat)[0]
      : { inclTax: 0, currencyCode: 'SEK' };

    const isFromPrice = cluster && cluster.length > 1 ? !productsHaveSamePrice(cluster) : false;

    const checkedStateLabel = checked
      ? `, ${formatMessage({ id: 'storageone.selectedStateAriaLabel' })}`
      : '';

    const dataTestId = cluster
      ? cluster.map(p => p.coreProductId)
      : product.type === ProductType.PAX_CORNER_SOLUTION_ASM
      ? product.coreProductId.split('-')[1]
      : product.coreProductId;

    return (
      <StyledCard>
        <StyledButton
          aria-disabled={disabled}
          data-is-disabled={disabled}
          data-testid={dataTestId}
          onClick={event => {
            disabled ? event.preventDefault() : onClick();
          }}
          aria-label={`${productTitleforAriaLabelInInfoButton}, ${product.designText}, ${price?.inclTax} ${price?.currencyCode}${checkedStateLabel}`}
          aria-pressed="false"
          aria-live="polite"
        >
          <StyledHeader>
            {productTypeIcon && (
              <IconTypeWrapper
                style={{ right: isRtl ? '0.25rem' : 'auto', left: isRtl ? 'auto' : '0.25rem' }}
              >
                {tooltipText ? (
                  <SkapaTooltip
                    tooltipText={tooltipText ? formatMessage({ id: tooltipText }) : ''}
                    tooltipButtonLabel={tooltipText ? formatMessage({ id: tooltipText }) : ''}
                    position="top"
                    interactiveCustomTrigger
                    boundaryContainerID="product-sidebar-toggle"
                  >
                    <Icon icon={productTypeIcon} pt="4px" />
                  </SkapaTooltip>
                ) : (
                  <Icon icon={productTypeIcon} pt="4px" />
                )}
              </IconTypeWrapper>
            )}
            <CheckboxIconWrapper style={{ left: isRtl ? 0 : 'auto', right: isRtl ? 'auto' : 0 }}>
              <animated.div style={scaleCounterProps}>
                <CheckboxIcon />
              </animated.div>
            </CheckboxIconWrapper>
          </StyledHeader>
          {mainCardImage && !children && (
            <ImageWrapper ref={imageRef}>
              <DraggableWrapper
                ref={drag}
                data-is-dragging={isDragging}
                data-is-disabled={disabled}
              >
                <ProductImage
                  sizes="9rem"
                  image={mainCardImage}
                  alt={product.name}
                  hoverImage={hoverCardImage}
                  hoverContainerClass={StyledCard}
                  onLoad={onLoad}
                />
              </DraggableWrapper>
            </ImageWrapper>
          )}
          {children}
          <CardInformationWrapper
            style={{ paddingRight: isRtl ? 0 : '1.3rem', paddingLeft: isRtl ? '1.3rem' : 0 }}
          >
            <StyledBox pt="xs">
              {title && (
                <StyledTitle
                  type={ThemeFontStyleTypeEnum.bodySmall}
                  translate="no"
                  style={{ textAlign: isRtl ? 'right' : 'left' }}
                >
                  {title}
                </StyledTitle>
              )}
              {subtitle && (
                <StyledSubtitle type={ThemeFontStyleTypeEnum.bodySmall}>{subtitle}</StyledSubtitle>
              )}
              {showPrice && (
                <PriceWrapper>
                  {lowestPriceProduct?.priceInformation?.currentPrice ? (
                    <PriceModule
                      shoppingProducts={shoppingProducts}
                      layout={PriceModuleLayoutTypeEnum.rowPrice}
                      isFromPrice={isFromPrice}
                      isCombinedProduct={product && product.parts.length > 1}
                      showPriceUnit={
                        shoppingProducts.length === 1 &&
                        product.priceInformation.nbrOfItemsInPackage > 1
                      }
                    />
                  ) : (
                    <Body type={ThemeFontStyleTypeEnum.bodyMedium} fontWeight="bold">
                      <Price amount={0} />
                    </Body>
                  )}
                </PriceWrapper>
              )}
            </StyledBox>
          </CardInformationWrapper>
          {(storeId || zipCode !== undefined) && showAvailability && (
            <Availability product={product} cluster={cluster} storeId={storeId} zipCode={zipCode} />
          )}
        </StyledButton>
        {showInfoBtn && (
          <InfoButtonWrapper
            style={{
              top: imageHeight + padding,
              left: isRtl ? 0 : 'auto',
              right: isRtl ? 'auto' : 0
            }}
          >
            <InfoButton
              onClick={onInfoClick}
              dataTestId="Product info"
              id={product.coreProductId}
              productTitle={productTitleforAriaLabelInInfoButton}
            />
          </InfoButtonWrapper>
        )}
      </StyledCard>
    );
  }
);

export default ProductCard;
